import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { submitRegistration, submitPartialRegistration, getRegistration, searchConsultants } from "@/lib/api";
import SpinWheelPreview from "@/components/SpinWheelPreview";
import { track } from "@/lib/analytics";
import { TID } from "@/constants/testIds";
import { Check, ChevronRight, ChevronLeft } from "lucide-react";
import BoardingPass from "@/components/BoardingPass";
import SpinWheel from "@/components/SpinWheel";

const REG_STORAGE_KEY = "phc-registration-id";

/**
 * 2-Step Chapter III Event Registration Form.
 *
 * Step 1 — Identity & Profile (typed inputs + dynamic occupation sub-fields)
 * Step 2 — Intent Signals (tap-based cards driving server-side lead scoring)
 *
 * Server computes lead band (Hot / Warm / Cold) from the intent signals.
 */

const CITY_OPTIONS = ["Mumbai", "Pune", "Other"];
const AGE_OPTIONS = ["25-35", "35-45", "45-55", "55+"];
const OCCUPATION_OPTIONS = [
  "Salaried professional",
  "Business owner/Entrepreneur",
  "Retired",
  "NRI professional",
];
const BUSINESS_FUNCTIONS = [
  "Sales & Marketing",
  "Finance & Accounts",
  "Operations & Supply",
  "Technology / Product",
  "Legal / Advisory",
  "HR & People",
  "Other",
];

const PURPOSE_OPTIONS = [
  "Coming to book a plot",
  "Seriously evaluating, likely to decide soon after",
  "Want to explore and understand the project first",
  "Just curious / exploring the area",
];
const SEARCH_DURATION_OPTIONS = [
  "Just started looking",
  "1-3 months",
  "3-6 months",
  "6+ months",
];
const FINANCING_OPTIONS = [
  "Ready to pay/self-funded",
  "Will need a home loan",
  "Still evaluating financing",
];
const VISIT_CONFIRMATION_OPTIONS = ["Yes, definitely", "Possibly", "Not able to currently"];
const EXPLORED_SIMILAR_OPTIONS = ["This region", "Elsewhere", "First time looking", "Currently comparing"];
const DECISION_INVOLVEMENT_OPTIONS = ["Just me", "Spouse/partner", "Extended family", "Group of friends"];
const WHAT_MATTERS_OPTIONS = [
  "Legal clarity",
  "Long-term appreciation",
  "Natural surroundings & lifestyle",
  "Family & community",
  "Price",
];
const HOME_USER_OPTIONS = [
  "Spouse & kids",
  "Parents/extended family",
  "Friends or an investment group",
  "Just myself",
];
const REFERRED_BY_OPTIONS = null; // deprecated — replaced by consultant typeahead

const initial = {
  // Step 1
  name: "",
  mobile: "",
  email: "",
  city: "Mumbai",
  age_bracket: "35-45",
  occupation: "Salaried professional",
  company: "",
  designation: "",
  business_function: "",

  // Step 2
  purpose: "",
  search_duration: "",
  financing: "",
  visit_confirmation: "",
  explored_similar: "",
  decision_involvement: "",
  what_matters: "",
  home_user: "",
  referred_by: "",
  referred_by_consultant_id: null,

  // Meta
  attendance_confirmed: true,
  consent: false,
};

const inputCls =
  "w-full border-0 border-b border-forest/25 bg-transparent py-3 px-0 text-forest placeholder:text-forest/40 focus:outline-none focus:border-forest text-[15px] font-body transition-colors";
const selectCls = inputCls + " appearance-none pr-6";

export default function EventRegistrationForm({ source = "event_in_site", onSubmitted }) {
  const [step, setStep] = useState(1);
  const [values, setValues] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [restoring, setRestoring] = useState(true);
  // Ref to the form container so we can scroll only to the top of the form
  // (not the whole page) when the user advances from Step 1 → Step 2.
  const containerRef = useRef(null);

  // On mount, try to restore an existing registration from localStorage so
  // that a page refresh does NOT let the user register again OR re-spin the
  // wheel. The backend enforces one-prize-per-registration atomically; this
  // just brings the UI back to the correct locked state.
  useEffect(() => {
    track("form_open", { form: "registration" });
    let cancelled = false;
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(REG_STORAGE_KEY) : null;
    if (!stored) {
      setRestoring(false);
      return () => {};
    }
    (async () => {
      try {
        const doc = await getRegistration(stored);
        if (!cancelled && doc?.id) setResult(doc);
      } catch {
        // Stale — clear it
        window.localStorage.removeItem(REG_STORAGE_KEY);
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [k]: val }));
  };

  const pick = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const validateStep1 = () => {
    if (!values.name.trim() || !values.mobile.trim() || !values.email.trim()) {
      toast.error("Please complete name, mobile and email.");
      return false;
    }
    // Dynamic occupation validation
    if (values.occupation === "Salaried professional") {
      if (!values.company.trim() || !values.designation.trim() || !values.business_function.trim()) {
        toast.error("Please fill in company, designation and function.");
        return false;
      }
    }
    if (values.occupation === "Business owner/Entrepreneur") {
      if (!values.company.trim()) {
        toast.error("Please share your company name.");
        return false;
      }
    }
    return true;
  };

  const validateStep2 = () => {
    const required = [
      "purpose",
      "search_duration",
      "financing",
      "visit_confirmation",
      "explored_similar",
      "decision_involvement",
      "what_matters",
      "home_user",
    ];
    for (const k of required) {
      if (!values[k]) {
        toast.error("Please answer every question to help us prepare a right-sized visit.");
        return false;
      }
    }
    if (!values.referred_by || !values.referred_by.trim()) {
      toast.error("Please tell us who referred you.");
      return false;
    }
    return true;
  };

  const next = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 1) {
      track("form_step_advance", { form: "registration", to_step: 2 });
      // Fire-and-forget partial save so we can measure Step 1 → Step 2 drop-off.
      submitPartialRegistration({
        name: values.name,
        mobile: values.mobile,
        email: values.email,
        city: values.city,
        age_bracket: values.age_bracket,
        occupation: values.occupation,
        company: values.company || null,
        designation: values.designation || null,
        business_function: values.business_function || null,
        source,
      }).catch(() => {
        /* silent — partial save is best-effort, does not gate progression */
      });
    }
    setStep((s) => Math.min(2, s + 1));
    // Scroll only to the top of the form container (not the whole page) so
    // the user immediately sees the Step 2 heading without losing context.
    // A small negative offset lifts the form so the "Step 2 of 2" progress
    // header is comfortably visible under the sticky site header.
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const targetY = window.scrollY + rect.top - 96;
      window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
    }
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    if (!values.consent) {
      toast.error("Please tick the consent box to register.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitRegistration({ ...values, source });
      setResult(res);
      try {
        window.localStorage.setItem(REG_STORAGE_KEY, res.id);
      } catch {
        /* ignore storage failures */
      }
      track("form_submit", {
        form: "registration",
        band: res?.lead_score?.band,
        score: res?.lead_score?.total,
        email: values.email,
        mobile: values.mobile,
        name: values.name,
      });
      toast.success("You're on the guest list.");
      onSubmitted?.(res);
    } catch (err) {
      track("form_error", { form: "registration" });
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (restoring) {
    return (
      <div className="border border-forest/20 rounded-lg p-8 bg-white text-center text-forest/60 text-sm">
        Loading your registration&hellip;
      </div>
    );
  }

  if (result) {
    const band = result.lead_score?.band || "Warm";
    const bandCopy = {
      Hot: "A senior member of our team will call within 24 hours.",
      Warm: "You will receive your confirmation and reminder shortly.",
      Cold: "Confirmation is on its way — no pressure, no follow-up you didn't ask for.",
    }[band];
    const restored = result.prize !== null && result.prize !== undefined;
    const clearAndRestart = () => {
      try {
        window.localStorage.removeItem(REG_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      setResult(null);
      setValues(initial);
      setStep(1);
    };
    return (
      <div className="space-y-8">
        <div className="space-y-3">
          <div className="w-12 h-12 rounded-full bg-lime grid place-items-center">
            <Check className="text-void" size={22} />
          </div>
          <div className="font-display text-3xl md:text-4xl text-forest leading-tight">
            You&apos;re registered for Chapter III.
          </div>
          <p className="text-sm text-forest/70 leading-relaxed max-w-lg">
            Sept 12–13, 2026. Above the clouds, at Pali. {bandCopy}
          </p>
          <div
            data-testid={TID.eventScoreBanner}
            className="inline-flex items-center gap-3 text-xs text-forest/60 bg-cream px-3 py-2 rounded-full"
          >
            <span className="w-2 h-2 rounded-full bg-teal" />
            Confirmation ID: {result.id.slice(0, 8).toUpperCase()}
          </div>
          {restored && (
            <p className="text-[11px] text-forest/50 max-w-lg">
              We recognised your device from a previous registration. Your boarding pass and prize are locked to your number — no re-spin possible.
            </p>
          )}
        </div>

        <BoardingPass registration={result} />

        <div className="border border-forest/15 rounded-2xl p-6 md:p-10 bg-offwhite">
          <SpinWheel
            registrationId={result.id}
            initialPrize={result.prize || null}
          />
        </div>

        <div className="text-center pt-2">
          <button
            type="button"
            onClick={clearAndRestart}
            data-testid="event-register-different"
            className="text-xs text-forest/50 hover:text-forest underline underline-offset-4"
          >
            Register a different attendee
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="border border-forest/20 rounded-lg overflow-hidden bg-white">
      {/* Progress */}
      <div className="px-6 pt-6">
        <div className="flex items-center justify-between text-xs text-forest/60 mb-2">
          <span className="eyebrow">Step {step} of 2</span>
          <span className="text-forest/40">
            {step === 1 ? "Identity & Profile" : "Intent Signals"}
          </span>
        </div>
        <div className="h-[2px] bg-forest/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-lime transition-all duration-500"
            style={{ width: `${(step / 2) * 100}%` }}
          />
        </div>
      </div>

      <form onSubmit={submit} className="p-6 md:p-8 space-y-6">
        {step === 1 && (
          <div data-testid={TID.eventStep1} className="space-y-5">
            <SpinWheelPreview />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Name" required>
                <input
                  data-testid={TID.eventName}
                  required
                  value={values.name}
                  onChange={set("name")}
                  className={inputCls}
                  placeholder="Full name"
                />
              </Field>
              <Field label="Mobile" required>
                <input
                  data-testid={TID.eventMobile}
                  required
                  inputMode="tel"
                  value={values.mobile}
                  onChange={set("mobile")}
                  className={inputCls}
                  placeholder="+91 98xxxxxxxx"
                />
              </Field>
              <Field label="Email" required>
                <input
                  data-testid={TID.eventEmail}
                  required
                  type="email"
                  value={values.email}
                  onChange={set("email")}
                  className={inputCls}
                  placeholder="you@company.com"
                />
              </Field>
              <Field label="City" required>
                <select
                  data-testid={TID.eventCity}
                  value={values.city}
                  onChange={set("city")}
                  className={selectCls}
                >
                  {CITY_OPTIONS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="Age bracket" required>
                <select
                  data-testid={TID.eventAgeBracket}
                  value={values.age_bracket}
                  onChange={set("age_bracket")}
                  className={selectCls}
                >
                  {AGE_OPTIONS.map((a) => (
                    <option key={a}>{a}</option>
                  ))}
                </select>
              </Field>
              <Field label="Occupation" required>
                <select
                  data-testid={TID.eventOccupation}
                  value={values.occupation}
                  onChange={set("occupation")}
                  className={selectCls}
                >
                  {OCCUPATION_OPTIONS.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </Field>
            </div>

            {/* Dynamic occupation sub-fields */}
            {(values.occupation === "Salaried professional" ||
              values.occupation === "NRI professional") && (
              <div className="grid md:grid-cols-3 gap-4 pt-2 border-t border-forest/10">
                <Field label="Company" required>
                  <input
                    data-testid={TID.eventCompany}
                    value={values.company}
                    onChange={set("company")}
                    className={inputCls}
                    placeholder="Company name"
                  />
                </Field>
                <Field label="Designation" required>
                  <input
                    data-testid={TID.eventDesignation}
                    value={values.designation}
                    onChange={set("designation")}
                    className={inputCls}
                    placeholder="e.g. VP, Product"
                  />
                </Field>
                <Field label="Function" required>
                  <select
                    data-testid={TID.eventBusinessFunction}
                    value={values.business_function}
                    onChange={set("business_function")}
                    className={selectCls}
                  >
                    <option value="">Select function…</option>
                    {BUSINESS_FUNCTIONS.map((f) => (
                      <option key={f}>{f}</option>
                    ))}
                  </select>
                </Field>
              </div>
            )}

            {values.occupation === "Business owner/Entrepreneur" && (
              <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-forest/10">
                <Field label="Company / Business" required>
                  <input
                    data-testid={TID.eventCompany}
                    value={values.company}
                    onChange={set("company")}
                    className={inputCls}
                    placeholder="Business name"
                  />
                </Field>
                <Field label="Industry / Sector">
                  <input
                    data-testid={TID.eventBusinessFunction}
                    value={values.business_function}
                    onChange={set("business_function")}
                    className={inputCls}
                    placeholder="e.g. Manufacturing, F&B, Tech"
                  />
                </Field>
              </div>
            )}

            {values.occupation === "Retired" && (
              <div className="pt-2 border-t border-forest/10">
                <Field label="Previous field / profession">
                  <input
                    data-testid={TID.eventDesignation}
                    value={values.designation}
                    onChange={set("designation")}
                    className={inputCls}
                    placeholder="e.g. Banking, Civil Services, Family Business"
                  />
                </Field>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div data-testid={TID.eventStep2} className="space-y-6">
            <p className="text-xs text-forest/60 leading-relaxed max-w-lg">
              A few short questions. There are no wrong answers — this helps our team prepare a right-sized visit for you.
            </p>

            <TapGroup
              testId={TID.eventPurpose}
              label="What best describes your purpose of visit?"
              options={PURPOSE_OPTIONS}
              value={values.purpose}
              onPick={(v) => pick("purpose", v)}
            />

            <TapGroup
              testId={TID.eventSearchDuration}
              label="How long have you been looking at second-home / plot options?"
              options={SEARCH_DURATION_OPTIONS}
              value={values.search_duration}
              onPick={(v) => pick("search_duration", v)}
            />

            <TapGroup
              testId={TID.eventFinancing}
              label="How would you fund this purchase?"
              options={FINANCING_OPTIONS}
              value={values.financing}
              onPick={(v) => pick("financing", v)}
            />

            <TapGroup
              testId={TID.eventVisitConfirmation}
              label="Are you able to visit the site at Pali if invited?"
              options={VISIT_CONFIRMATION_OPTIONS}
              value={values.visit_confirmation}
              onPick={(v) => pick("visit_confirmation", v)}
            />

            <TapGroup
              testId={TID.eventExploredSimilar}
              label="Have you explored similar projects before?"
              options={EXPLORED_SIMILAR_OPTIONS}
              value={values.explored_similar}
              onPick={(v) => pick("explored_similar", v)}
            />

            <TapGroup
              testId={TID.eventDecisionInvolvement}
              label="Who else is involved in this decision?"
              options={DECISION_INVOLVEMENT_OPTIONS}
              value={values.decision_involvement}
              onPick={(v) => pick("decision_involvement", v)}
            />

            <TapGroup
              testId={TID.eventWhatMatters}
              label="What matters most to you in a purchase like this?"
              options={WHAT_MATTERS_OPTIONS}
              value={values.what_matters}
              onPick={(v) => pick("what_matters", v)}
            />

            <TapGroup
              testId={TID.eventHomeUser}
              label="Who will use this home the most?"
              options={HOME_USER_OPTIONS}
              value={values.home_user}
              onPick={(v) => pick("home_user", v)}
            />

            <ConsultantTypeahead
              value={values.referred_by}
              onSelect={(picked) => {
                setValues((v) => ({
                  ...v,
                  referred_by: picked.label,
                  referred_by_consultant_id: picked.id,
                }));
              }}
              onFreeText={(text) => {
                setValues((v) => ({
                  ...v,
                  referred_by: text,
                  referred_by_consultant_id: null,
                }));
              }}
            />

            <label className="flex items-start gap-3 pt-4 border-t border-forest/10">
              <input
                data-testid={TID.eventConsent}
                type="checkbox"
                required
                checked={values.consent}
                onChange={set("consent")}
                className="mt-1 w-4 h-4 accent-[color:var(--phc-forest)]"
              />
              <span className="text-xs text-forest/80 leading-relaxed">
                I consent to being contacted via WhatsApp, Call, and SMS regarding Pali Hill City Chapter III.
              </span>
            </label>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-forest/10">
          {step > 1 ? (
            <button
              type="button"
              onClick={prev}
              data-testid={TID.eventPrev}
              className="btn-outline-forest text-sm"
            >
              <ChevronLeft size={16} /> Back
            </button>
          ) : (
            <span />
          )}

          {step < 2 ? (
            <button
              type="button"
              onClick={next}
              data-testid={TID.eventNext}
              className="btn-primary text-sm"
            >
              Continue <ChevronRight size={16} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={submitting}
              data-testid={TID.eventSubmit}
              className="btn-primary text-sm"
            >
              {submitting ? "Registering…" : "Register for Chapter III"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="eyebrow text-forest/60 block mb-1">
        {label} {required && <span className="text-teal">*</span>}
      </span>
      {children}
    </label>
  );
}

function TapGroup({ testId, label, options, value, onPick }) {
  return (
    <div data-testid={testId}>
      <div className="eyebrow text-forest/60 mb-3">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const on = value === opt;
          return (
            <button
              type="button"
              key={opt}
              onClick={() => onPick(opt)}
              className={`text-left px-4 py-2.5 rounded-full border text-[13px] transition-colors ${
                on
                  ? "bg-forest text-cream border-forest"
                  : "border-forest/25 text-forest hover:border-forest"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
 * ConsultantTypeahead — searchable input backed by /api/consultants/search
 * ------------------------------------------------------------------ */
function ConsultantTypeahead({ value, onSelect, onFreeText }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return () => {};
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const rows = await searchConsultants(query.trim(), 8);
        setResults(rows);
        setOpen(rows.length > 0);
        setHighlight(0);
      } catch {
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(handle);
  }, [query]);

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(results.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pickResult(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const pickResult = (r) => {
    if (!r) return;
    // Users remember the representative they spoke to — fall back to name
    // for legacy rows that pre-date the representative_name column.
    const displayName = r.representative_name || r.name || "";
    setQuery(displayName);
    setOpen(false);
    onSelect({ id: r.id, label: displayName });
  };

  return (
    <div>
      <div className="eyebrow text-forest/60 mb-2">
        Who referred you to submit this form? <span className="text-teal">*</span>
      </div>
      <div className="relative">
        <input
          type="text"
          data-testid={TID.eventReferredBy}
          value={query}
          onChange={(e) => {
            const val = e.target.value;
            setQuery(val);
            onFreeText(val);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          placeholder="Start typing the representative's name — or type Direct if none"
          className="w-full border-0 border-b border-forest/25 bg-transparent py-3 px-0 text-forest placeholder:text-forest/40 focus:outline-none focus:border-forest text-[15px] font-body transition-colors"
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-0 top-3 text-[11px] text-forest/40">Searching…</div>
        )}
        {open && results.length > 0 && (
          <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border border-forest/20 rounded-lg shadow-lg overflow-hidden max-h-72 overflow-y-auto">
            {results.map((r, i) => (
              <li
                key={r.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickResult(r);
                }}
                className={`px-4 py-2.5 cursor-pointer text-sm ${
                  i === highlight ? "bg-cream" : "hover:bg-cream/60"
                }`}
              >
                <div className="text-forest">{r.representative_name || r.name}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="mt-2 text-[11px] text-forest/50">
        If you weren&apos;t referred, type <em>Direct</em>. Otherwise start typing the
        representative&apos;s name.
      </p>
    </div>
  );
}

